import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { retryWithBackoff } from '@/utils/retry';
import { getSessionItem, setSessionItem, removeSessionItem } from '@/utils/storage';

interface PasswordStrength {
  score: number;
  feedback: string;
  color: string;
}

export function ResetPasswordPage() {
  // Local state to hold the reset token.
  const [resetToken, setResetToken] = useState<string | null>(null);
  // Flag to indicate when token verification is complete.
  const [tokenChecked, setTokenChecked] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [strength, setStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: '',
    color: 'bg-gray-200'
  });

  const navigate = useNavigate();
  const { toast } = useToast();

  // On mount, verify that we have a valid reset token.
  useEffect(() => {
    // Try to get the token from sessionStorage.
    let token = getSessionItem<string | null>('resetToken', null);

    // Parse the URL for token parameters.
    const hash = window.location.hash;
    const search = window.location.search;
    const hashParams = new URLSearchParams(hash.replace('#', '?'));
    const searchParams = new URLSearchParams(search);

    // Look for token under common keys: "token", "access_token", or "code"
    const urlToken =
      hashParams.get('token') ||
      searchParams.get('token') ||
      hashParams.get('access_token') ||
      searchParams.get('access_token') ||
      hashParams.get('code') ||
      searchParams.get('code');

    // If a token is found in the URL, store it in sessionStorage.
    if (urlToken) {
      setSessionItem('resetToken', urlToken);
      token = urlToken;
    }

    if (token) {
      setResetToken(token);
    } else {
      // Instead of an immediate redirect, show a friendly error.
      toast({
        title: 'Invalid Reset Link',
        description: 'No valid reset token found. Please request a new password reset link.',
        variant: 'destructive'
      });
    }

    setTokenChecked(true);
  }, [toast]);

  // Password strength calculation.
  const calculatePasswordStrength = (value: string): PasswordStrength => {
    let score = 0;
    let feedback = '';

    // Check different criteria.
    if (value.length >= 8) score += 20;
    if (/[A-Z]/.test(value)) score += 20;
    if (/[a-z]/.test(value)) score += 20;
    if (/\d/.test(value)) score += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(value)) score += 20;

    if (score === 100) {
      feedback = 'Strong password';
    } else if (score >= 60) {
      feedback = 'Moderate password';
    } else {
      feedback = 'Weak password';
    }

    const color =
      score === 100 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500';

    return { score, feedback, color };
  };

  // Validate that the password meets requirements.
  const validatePassword = (value: string): string | null => {
    if (value.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
    if (!/\d/.test(value)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
      return 'Password must contain at least one special character';
    return null;
  };

  // Handle the form submission to update the password.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure a reset token is present.
    if (!resetToken) {
      toast({
        title: 'Error',
        description: 'Reset token is missing. Please request a new password reset link.',
        variant: 'destructive'
      });
      navigate('/auth');
      return;
    }

    // Validate the new password.
    const validationError = validatePassword(password);
    if (validationError) {
      toast({
        title: 'Invalid Password',
        description: validationError,
        variant: 'destructive'
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      // Update the user's password.
      const { error } = await retryWithBackoff(() =>
        supabase.auth.updateUser({ password })
      );
      if (error) throw error;

      // Clear the stored token upon success.
      removeSessionItem('resetToken');

      toast({
        title: 'Success',
        description: 'Your password has been reset successfully.'
      });

      // Redirect the user to the login page.
      navigate('/auth');
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to reset password. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update the password strength as the user types.
  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setStrength(calculatePasswordStrength(value));
  };

  // Until token verification is complete, show a loading indicator.
  if (!tokenChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="ml-2">Verifying reset link...</p>
      </div>
    );
  }

  // If no valid token is found, show an error message with an option to go back.
  if (!resetToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Invalid Reset Link</h1>
          <p className="text-sm text-muted-foreground mt-2">
            No valid reset token was found. Please request a new password reset link.
          </p>
          <Button onClick={() => navigate('/auth')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  // Render the reset password form.
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Reset Password</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Please enter your new password below.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                className="pr-10"
                required
                aria-describedby="password-strength"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {/* Password Strength Indicator */}
            <div className="space-y-2" id="password-strength">
              <Progress
                value={strength.score}
                className={cn("h-2", strength.color)}
                aria-label="Password strength"
              />
              <p className="text-sm text-muted-foreground">{strength.feedback}</p>
            </div>
          </div>
          {/* Confirm Password Field */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pr-10"
                required
              />
            </div>
          </div>
          {/* Submit Button */}
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reset Password
          </Button>
        </form>
        {/* Password Requirements */}
        <div className="rounded-lg bg-muted p-4">
          <h2 className="text-sm font-medium mb-2">Password Requirements:</h2>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• At least 8 characters long</li>
            <li>• At least one uppercase letter</li>
            <li>• At least one lowercase letter</li>
            <li>• At least one number</li>
            <li>• At least one special character</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
